import { useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactInfoCards = ({ contactInfo, t }) => (
  <div className="space-y-4">
    {contactInfo.map((item) => (
      <a
        key={item.label}
        href={item.href}
        target={item.href.startsWith("http") ? "_blank" : undefined}
        rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
        data-testid={`contact-${item.label.toLowerCase()}`}
        className="block"
      >
        <Card className="border-[#003A4B]/10 hover:border-[#C89D4B]/40 hover:shadow-lg transition-all group cursor-pointer">
          <CardContent className="flex items-center gap-4 p-6">
            <div className="w-14 h-14 bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <item.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-[#718096]">{item.label}</p>
              <p className="font-semibold text-[#1A2332]">{item.value}</p>
            </div>
          </CardContent>
        </Card>
      </a>
    ))}

    <Card className="border-[#003A4B]/10 bg-[#F3F0E7]">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-[#C89D4B] to-[#D4B06A] rounded-2xl flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#718096]">{t.contact.serviceArea}</p>
            <p className="font-semibold text-[#1A2332]">Wilmington, Delaware</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="rounded-2xl overflow-hidden border border-[#003A4B]/10 shadow-sm mt-2" data-testid="google-map">
      <iframe
        title="Caceres Essential Care Service Area"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d97956.37792964089!2d-75.61468!3d39.7391!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6fd3eee04a66b%3A0xc892cd53b6c9120d!2sWilmington%2C%20DE!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
        width="100%"
        height="200"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      ></iframe>
    </div>
  </div>
);

const ContactForm = ({ t }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="lg:col-span-2 border-0 shadow-xl">
      <CardHeader>
        <CardTitle className="text-[#1A2332]">{t.contact.sendMessage}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && <div data-testid="contact-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#1A2332]">{t.contact.fullName}</Label>
              <Input id="name" data-testid="contact-name-input" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} placeholder={t.contact.yourName} required className="border-[#003A4B]/20 focus:ring-[#003A4B] focus:border-[#003A4B]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1A2332]">{t.contact.email}</Label>
              <Input id="email" type="email" data-testid="contact-email-input" value={formData.email} onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))} placeholder={t.contact.yourEmail} required className="border-[#003A4B]/20 focus:ring-[#003A4B] focus:border-[#003A4B]" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-[#1A2332]">{t.contact.phone}</Label>
            <Input id="phone" type="tel" data-testid="contact-phone-input" value={formData.phone} onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))} placeholder={t.contact.phonePlaceholder} className="border-[#003A4B]/20 focus:ring-[#003A4B] focus:border-[#003A4B]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message" className="text-[#1A2332]">{t.contact.message}</Label>
            <Textarea id="message" data-testid="contact-message-input" value={formData.message} onChange={(e) => setFormData(f => ({ ...f, message: e.target.value }))} placeholder={t.contact.messagePlaceholder} rows={4} required className="border-[#003A4B]/20 focus:ring-[#003A4B] focus:border-[#003A4B]" />
          </div>
          <Button type="submit" data-testid="contact-submit-btn" className="w-full bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all" disabled={submitted || loading}>
            {submitted ? (<><CheckCircle2 className="w-5 h-5 mr-2" />{t.contact.sent}</>) : t.contact.submit}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export const ContactSection = () => {
  const { t } = useLanguage();

  const contactInfo = [
    { icon: Phone, label: t.contact.phone, value: "(609) 315-3943", href: "tel:+16093153943" },
    { icon: MessageCircle, label: t.contact.whatsapp, value: t.contact.whatsappText, href: "https://wa.me/16093153943" },
    { icon: Mail, label: t.contact.email, value: "servicies@caceres-essentialcare.com", href: "mailto:servicies@caceres-essentialcare.com" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-gradient-to-b from-white to-[#F3F0E7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">{t.contact.title}</span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332] mt-3">{t.contact.subtitle}</h2>
          <p className="text-[#718096] mt-4">{t.contact.description}</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          <ContactInfoCards contactInfo={contactInfo} t={t} />
          <ContactForm t={t} />
        </div>
      </div>
    </section>
  );
};
